import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { CsrfService } from '../services/csrf.service';
import { InactivityLogoutService } from '../services/inactivity-logout.service';
import { catchError, finalize, from, Observable, of, shareReplay, switchMap, throwError } from 'rxjs';

/**
 * HTTP interceptor that:
 *
 * 1. Attaches `withCredentials: true` so the browser sends HttpOnly auth cookies.
 *
 * 2. Injects the X-XSRF-TOKEN header on all state-changing requests by reading
 *    the in-memory CSRF token from CsrfService. This avoids the cross-origin
 *    cookie-reading limitation that prevents the built-in Angular XSRF mechanism
 *    from working when the SPA and API are on different origins.
 *
 * 3. On a 401 response, attempts a single silent token refresh via
 *    POST /authentication/refresh.  If the refresh succeeds the original
 *    request is replayed.  If it fails, the session is cleared and the user is
 *    redirected to /auth.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private static readonly XSRF_HEADER = 'X-XSRF-TOKEN';
  private static readonly MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

  /**
   * Endpoints that are part of the login/auth flow and therefore must not
   * trigger a token-refresh on 401 (they have no valid session to refresh),
   * and should not require a CSRF token prime before being sent.
   */
  private static readonly AUTH_FLOW_ENDPOINTS = [
    '/authentication/login',
    '/authentication/refresh',
    '/authentication/logout',
    '/authentication/enable-mfa',
    '/authentication/verify-mfa',
    '/authentication/email-otp/send',
    '/authentication/email-otp/verify',
    '/authentication/verify-token',
    '/authentication/accept-invitation',
    '/authentication/forgot-password',
  ];

  private static readonly BACKGROUND_ENDPOINTS = [
    '/authentication/me',
  ];

  /** Shared by every request that receives a 401 during the same refresh. */
  private refreshInProgress$: Observable<void> | null = null;

  constructor(
    private authService: AuthService,
    private csrfService: CsrfService,
    private inactivityLogoutService: InactivityLogoutService
  ) { }

  private isAuthFlowEndpoint(url: string): boolean {
    return AuthInterceptor.AUTH_FLOW_ENDPOINTS.some((ep) => url.includes(ep));
  }

  private isBackgroundEndpoint(url: string): boolean {
    return AuthInterceptor.BACKGROUND_ENDPOINTS.some((ep) => url.includes(ep));
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isAuthFlowEndpoint(req.url) && !this.isBackgroundEndpoint(req.url)) {
      this.inactivityLogoutService.registerActivity('http');
    }

    return this.sendWithCredentialsAndCsrf(req, next, false).pipe(
      catchError((error: HttpErrorResponse) => {
        // Only handle 401 Unauthorized — and only for our own API calls.
        // Auth-flow endpoints (login, enable-mfa, verify-mfa, etc.) run before
        // a full session exists, so a 401 from them must NOT trigger a refresh
        // attempt — that would just produce another 401 and force-logout the user.
        const isAuthEndpoint = this.isAuthFlowEndpoint(req.url);

        if (error.status === 401 && !isAuthEndpoint) {
          return this.handle401(req.clone({ withCredentials: true }), next);
        }

        // A 403 can mean the CSRF token is stale, or that the access cookie
        // expired before Spring could compare any token. Re-fetch first; if
        // there is no cookie-bound token, renew the access cookie as well.
        if (error.status === 403 && AuthInterceptor.MUTATING_METHODS.has(req.method)) {
          const alreadyRetried = req.headers.has('X-CSRF-RETRY');
          if (!alreadyRetried) {
            return from(this.csrfService.fetchToken()).pipe(
              switchMap(() => {
                if (!this.csrfService.getToken()) {
                  return this.handle401(req, next);
                }
                return this.sendWithCredentialsAndCsrf(req, next, true).pipe(
                  catchError((retryError: HttpErrorResponse) =>
                    retryError.status === 401 && !isAuthEndpoint
                      ? this.handle401(req, next)
                      : throwError(() => retryError)
                  )
                );
              })
            );
          }
        }

        return throwError(() => error);
      })
    );
  }

  private sendWithCredentialsAndCsrf(
    req: HttpRequest<any>,
    next: HttpHandler,
    retrying: boolean
  ): Observable<HttpEvent<any>> {
    // Auth-flow endpoints (login, MFA setup, etc.) run before a session exists
    // and do not require a CSRF token — skip priming entirely for them.
    const needsCsrf =
      AuthInterceptor.MUTATING_METHODS.has(req.method) &&
      !this.isAuthFlowEndpoint(req.url);

    // If this is a mutating request and no token is cached yet, fetch it first
    // so the request goes out with the header on the first attempt.
    const prime$: Observable<void> =
      needsCsrf && !this.csrfService.getToken() && !retrying
        ? from(this.csrfService.fetchToken())
        : of(undefined);

    return prime$.pipe(
      switchMap(() => {
        if (needsCsrf && !this.csrfService.getToken()) {
          // The public CSRF endpoint has no token without an access cookie.
          // Refresh the cookie before sending a write request.
          return retrying
            ? throwError(() => new HttpErrorResponse({ status: 401, statusText: 'No authenticated CSRF token' })) // i18n-ignore
            : this.handle401(req, next);
        }

        const headers: { [name: string]: string } = {};
        if (needsCsrf) {
          const csrfToken = this.csrfService.getToken();
          if (csrfToken) {
            headers[AuthInterceptor.XSRF_HEADER] = csrfToken;
          }
        }

        const credentialed = req.clone({
          withCredentials: true,
          setHeaders: {
            ...headers,
            ...(retrying ? { 'X-CSRF-RETRY': '1' } : {}),
          },
        });

        return next.handle(credentialed);
      })
    );
  }

  /**
   * Attempt a silent refresh.  If refresh succeeds, replay the failed request.
   * If refresh fails (refresh token expired), clear session and redirect.
   */
  private handle401(
    failedReq: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.getOrStartRefresh().pipe(
      // A refreshed access cookie changes the JWT-bound CSRF value. Rebuild the
      // failed request with the new token before replaying it.
      switchMap(() => this.sendWithCredentialsAndCsrf(failedReq, next, true))
    );
  }

  private getOrStartRefresh(): Observable<void> {
    if (!this.refreshInProgress$) {
      this.csrfService.clearToken();

      this.refreshInProgress$ = this.authService.refreshAccessToken().pipe(
        switchMap(() => from(this.csrfService.fetchToken())),
        switchMap(() => this.csrfService.getToken()
          ? of(undefined)
          : throwError(() => new HttpErrorResponse({ status: 401, statusText: 'No authenticated CSRF token' }))), // i18n-ignore
        catchError((refreshError) => {
          this.authService.clearSessionState();
          return throwError(() => refreshError);
        }),
        finalize(() => {
          this.refreshInProgress$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.refreshInProgress$;
  }
}
